import React, { useState } from 'react';
import './InfoCard.css';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const InfoCard = ({ title, color, data }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const toggleExpand = () => setIsExpanded(!isExpanded);

    const filteredData = data.filter(row => {
        return Object.values(row).some(value =>
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    // Helper function to format the date (DD/MM/YYYY to YYYY-MM-DD)
    const formatDateToComparable = (date) => {
        const [day, month, year] = date.split('/');
        return new Date(`${year}-${month}-${day}`);
    };

    // Updated calculateDaysToExpiry using the formatted date
    const calculateDaysToExpiry = (expiryDate) => {
        const expiry = formatDateToComparable(expiryDate);
        const today = new Date();
        const timeDiff = expiry - today;
        return Math.ceil(timeDiff / (1000 * 3600 * 24)); 
    };

    // Sort the data based on stock levels for Low Stock Meds and expiry for Expiring Meds
    const sortedData = () => {
        if (title === 'Low Stock Meds') {
            return filteredData.sort((a, b) => a.tablets - b.tablets); // Sort by stock in ascending order
        } else if (title === 'Expiring Meds') {
            return filteredData.sort((a, b) => calculateDaysToExpiry(a.expiryDate) - calculateDaysToExpiry(b.expiryDate)); // Sort by days to expiry in ascending order
        } else if (title === 'Expired Meds') {
            return filteredData.sort((a, b) => formatDateToComparable(a.expiryDate) - formatDateToComparable(b.expiryDate)); // Sort expired meds by expiry date
        }
        return filteredData;
    };

    // Determine if a medication is expired or has negative stock
    const isExpired = (expiryDate, stockLevel) => {
        const daysToExpiry = calculateDaysToExpiry(expiryDate);
        return daysToExpiry < 0 || stockLevel < 0;
    };

    const generatePDF = () => {
        const doc = new jsPDF();
        const tableColumns = [];

        if (title === 'Low Stock Meds' || title === 'Expired Meds' || title === 'Expiring Meds') {
            tableColumns.push(['Batch Number', 'Medicine Name', 'Expiry Date', 'Stock Level']);
        }

        const tableRows = sortedData().map(row => [
            row.batchNumber,
            row.name,
            isExpired(row.expiryDate, row.tablets) ? 'EXPIRED' : `${row.expiryDate} (${calculateDaysToExpiry(row.expiryDate)} days)`, 
            row.tablets,
        ]);

        doc.autoTable({
            head: tableColumns,
            body: tableRows,
        });

        doc.save(`${title}.pdf`);
    };

    return (
        <div className={`infocard ${isExpanded ? 'expanded' : ''}`} style={{ backgroundColor: '#024950' }}>
            <div className="color-indicator" style={{ backgroundColor: color }}></div>

            <div className="infocard-content">
                <h3 className="infocard-title">{title}</h3>
                <div className="infocard-number">{filteredData.length}</div>
                <div className='infocard-subtext'>Last 2 weeks</div>
                <button className="infocard-btn" onClick={toggleExpand}>
                    {isExpanded ? 'Show Less' : 'Know More'}
                </button>
            </div>

            {isExpanded && (
                <div className="infocard-expanded">
                    <div className="filter-container">
                        <input
                            type="text"
                            className="filter-input"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <table className="infocard-table">
                        <thead>
                            <tr>
                                <th>Batch Number</th>
                                <th>Medicine Name</th>
                                <th>Expiry Date</th>
                                <th>Stock Level</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData().map((row, index) => (
                                <tr key={index}>
                                    <td>{row.batchNumber}</td>
                                    <td>{row.name}</td>
                                    <td>{isExpired(row.expiryDate, row.tablets) ? 'EXPIRED' : `${row.expiryDate} (${calculateDaysToExpiry(row.expiryDate)} days)`}</td>
                                    <td>{row.tablets}</td> 
                                    <td>
                                        <button className="inventory-btn">View In Inventory</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="download-button-container">
                        <button className="download-btn" onClick={generatePDF}>
                            Download PDF
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InfoCard;
